import Model, { attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import { service } from '@ember/service'
import { join } from 'teacher-ui-client/utils/uri'
import { capitalize } from '@ember/string'
import config from 'teacher-ui-client/config/environment'
import type { IntlService } from 'ember-intl'
import type BookLibraryBookMetadata from 'teacher-ui-client/models/book-library/book-metadata'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type BooleanTransform from 'teacher-ui-client/transforms/boolean'

export enum BookClassification {
  Fiction = 'fiction',
  NonFiction = 'nonfiction',
}

export default class BookLibraryBook extends Model {
  @service intl!: IntlService

  @attr<StringTransform>('string') declare code: string

  // no transform, just the raw array of strings
  @attr() declare primaryCategories: string[]

  @attr<StringTransform>('string') declare classification: BookClassification

  @attr<StringTransform>('string') declare series: string

  @attr<BooleanTransform>('boolean') declare exclude: boolean

  @attr() declare bigBookLevels?: Record<SubscriptionType, string | undefined>

  // sync relationship, metadata record will always be loaded along with the book record
  @belongsTo<BookLibraryBookMetadata>('book-library/book-metadata', { async: false, inverse: 'book' })
  declare bookMetadata: BookLibraryBookMetadata

  get title() {
    return this.bookMetadata.name
  }

  get description() {
    return this.bookMetadata.description
  }

  get pages() {
    return this.bookMetadata.pages
  }

  get chapters() {
    return this.bookMetadata.chapters
  }

  get wordCount() {
    return this.bookMetadata.wordCount
  }

  get hasNarrationAudio() {
    return this.bookMetadata.narrationAudio
  }

  get hasQuiz() {
    return this.bookMetadata.hasQuiz
  }

  get bookPreviewImg() {
    if (!this.bookMetadata) return
    const { coverPreviewImg } = this.bookMetadata
    const url = join(config.libraryAssetsUrl, coverPreviewImg.path)
    return { ...coverPreviewImg, url }
  }

  get bookFullImg() {
    if (!this.bookMetadata) return
    const { coverFullImg } = this.bookMetadata
    const url = join(config.libraryAssetsUrl, coverFullImg.path)
    return { ...coverFullImg, url }
  }

  get formattedClassification() {
    return this.intl.t(`classifications.${this.classification}`)
  }

  get formattedPrimaryCategories() {
    return this.primaryCategories.map((categoryId) => {
      return this.intl.t(`bookLibrary.categories.${categoryId}`)
    })
  }

  get formattedSeries() {
    return capitalize(this.series).split('_').join(' ')
  }

  get formattedAudio() {
    return this.hasNarrationAudio ? this.intl.t('bookLibrary.audio.yes') : this.intl.t('bookLibrary.audio.no')
  }

  [Type] = 'book-library/book' as const
}
